import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik'
import * as Yup from "yup"
import { Link as RouterLink } from "react-router-dom"
import { Icon } from "@iconify/react"
import React, { useState } from 'react'
import { Box, CircularProgress, IconButton, InputAdornment, Link, TextField, Tooltip } from "@mui/material"
import { LoadingButton } from "@mui/lab"

import eyeFill from "@iconify/icons-eva/eye-fill"
import eyeOffFill from "@iconify/icons-eva/eye-off-fill"

export interface ILoginValues {
    email: string,
    password: string
}

export interface ILoginSocial {
    email: string,
    name: string
}

interface ILoginFormProps {
    handleSubmitProp: (values: ILoginValues, actions: FormikHelpers<ILoginValues>) => void,
}

const LoginForm: React.FC<ILoginFormProps> = ({ handleSubmitProp }) => {
    const [showPassword, setShowPassword] = useState(false)

    const LoginSchema = Yup.object().shape({
        email: Yup.string().email('Endereço de e-mail inválido').required('E-mail obrigatório'),
        password: Yup.string().required('Senha obrigatória')
    })

    const formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: LoginSchema,
        onSubmit: (values, actions) => handleSubmitProp(values, actions)
    })

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik

    return (
        <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <TextField
                    fullWidth
                    autoComplete="email"
                    type="email"
                    label="Email"
                    placeholder="seu@email.com"
                    {...getFieldProps('email')}
                    inputProps={{ 'data-testid': 'email' }}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2.5 }}
                />

                <TextField
                    fullWidth
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    label="Senha"
                    placeholder="••••••••"
                    {...getFieldProps('password')}
                    inputProps={{ 'data-testid': 'password' }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={showPassword ? "Ocultar senha" : "Mostrar senha"} placement="top">
                                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    sx={{ mb: 1 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Link
                        component={RouterLink}
                        to="/recoverPassword"
                        sx={{ fontSize: '12.5px', color: 'text.secondary', textDecoration: 'none' }}
                    >
                        Esqueci minha senha
                    </Link>
                </Box>

                <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress color="inherit" size={18} />}
                    sx={{ height: 54, fontSize: '15px', fontWeight: 600 }}
                >
                    Entrar
                </LoadingButton>
            </Form>
        </FormikProvider>
    )
}

export default LoginForm
